// Sprint 9 — Notificaciones y recordatorios (toggles en Professional.notify24h / notify1h → PATCH /api/dashboard/me)
import { useEffect, useState } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/useToast";

export type ClientReminderPrefs = {
  notify24h: boolean;
  notify1h: boolean;
};

export function NotifReminderToggles({
  initial,
  onPrefsChange,
}: {
  initial: ClientReminderPrefs;
  onPrefsChange?: (p: ClientReminderPrefs) => void;
}) {
  const { refresh } = useAuth();
  const toast = useToast();
  const [prefs, setPrefs] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPrefs(initial);
  }, [initial.notify24h, initial.notify1h]);

  async function patchMe(partial: Partial<ClientReminderPrefs>) {
    const prev = { ...prefs };
    const next = { ...prefs, ...partial };
    setPrefs(next);
    setSaving(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(partial),
      },
      refresh,
    );
    setSaving(false);
    if (r.ok) {
      onPrefsChange?.(next);
      toast.success("Guardado");
    } else {
      setPrefs(prev);
      toast.error("No se pudo guardar.");
    }
  }

  const row = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "4",
    py: "4",
    borderBottomWidth: "1px",
    borderColor: "#e2e8f0",
    _last: { borderBottomWidth: "0" },
  });

  return (
    <div className={cx(cf.dashSurface, css({ p: { base: "4", md: "6" } }))}>
      <p className={css({ fontSize: "xs", color: "#64748b", mb: "4" })}>
        {saving
          ? "Guardando…"
          : "Los cambios se guardan al tocar cada interruptor. Se envían por email al cliente; en plan Pro también por WhatsApp si lo tenés activo en Avisos."}
      </p>
      <div>
        <div className={row}>
          <div>
            <p className={css({ fontSize: "sm", color: "#0f172a", fontWeight: "medium" })}>Recordatorio ~24 h antes</p>
            <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>Reduce ausencias.</p>
          </div>
          <Toggle checked={prefs.notify24h} onChange={(v) => void patchMe({ notify24h: v })} />
        </div>
        <div className={row}>
          <div>
            <p className={css({ fontSize: "sm", color: "#0f172a", fontWeight: "medium" })}>Recordatorio ~1 h antes</p>
            <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>Email y WhatsApp (Pro) de último aviso.</p>
          </div>
          <Toggle checked={prefs.notify1h} onChange={(v) => void patchMe({ notify1h: v })} />
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={css({
        w: "11",
        h: "6",
        rounded: "full",
        position: "relative",
        flexShrink: 0,
        transition: "background 0.2s",
        bg: checked ? "rgba(129, 140, 248, 0.9)" : "#e2e8f0",
        borderWidth: "1px",
        borderColor: checked ? "rgba(99,102,241,0.4)" : "#cbd5e1",
        cursor: "pointer",
      })}
    >
      <span
        className={css({
          position: "absolute",
          top: "3px",
          left: checked ? "calc(100% - 22px)" : "3px",
          w: "18px",
          h: "18px",
          rounded: "full",
          bg: "white",
          transition: "left 0.2s",
          boxShadow: "sm",
        })}
      />
    </button>
  );
}
